/**
 * Family guard: every family member drawn must come from the family photo.
 * Pure lexicon helpers. Imports nothing (worker-safe, client-safe).
 * GRANDPARENT_WORD / GRANDPARENT_MIN_AGE are duplicated in lib/picture-book/central-roles.ts (parity test).
 */

/**
 * `parent` = any adult who is not a grandparent (fills the aunt / uncle / "parent" words, as before); `mother` /
 * `father` = an adult who can play mum / dad: labelled so, or a generic parent (no label, "parent", or any label that
 * is not an aunt / uncle or a named parent). Only labels make the mum / dad split bite (final review I2).
 */
export type FamilyKind = 'grandparent' | 'parent' | 'mother' | 'father' | 'grownup' | 'sibling';
export type GuardPerson = {
  name: string;
  age?: number | string | null;
  relationship?: string | null;
  isMainCharacter?: boolean;
};

export const GRANDPARENT_WORD =
  /\b(?:grand(?:parent|ma|pa|mother|father|dad|ad)|granny|gran|nana|nani|dadi|nan|nonna|nonno|abuela|abuelo|oma|opa|paati|thatha|lola|lolo)s?\b/i;
export const GRANDPARENT_MIN_AGE = 55;

const ADULT_RELATIONSHIP = /\b(?:mum|mom|mummy|mommy|mama|papa|amma|appa|dad|daddy|mother|father|parent|aunt|auntie|aunty|uncle)s?\b/i;
const MOTHER_WORDS = '(?:mum|mom|mummy|mommy|mama|amma|mother)s?';
const FATHER_WORDS = '(?:dad|daddy|father|papa|appa)s?';
const MOTHER_REL = new RegExp(`\\b${MOTHER_WORDS}\\b`, 'i');
const FATHER_REL = new RegExp(`\\b${FATHER_WORDS}\\b`, 'i');
const AUNT_UNCLE_REL = /\b(?:aunt|auntie|aunty|uncle)s?\b/i;

/** Copy of ageInYears (lib/picture-book/casting.ts); kept local so this module imports nothing. */
function ageInYears(age: unknown): number | undefined {
  if (typeof age === 'number') return Number.isFinite(age) && age >= 0 ? age : undefined;
  if (typeof age !== 'string') return undefined;
  const s = age.trim().toLowerCase();
  if (!s) return undefined;
  if (/^(new[\s-]?born|baby|infant)$/.test(s)) return 0;
  const m = /^(\d+(?:\.\d+)?)\s*(y|yr|yrs|year|years|m|mo|mos|month|months|w|wk|wks|week|weeks)?\.?(?:\s*old)?$/.exec(s);
  if (!m) return undefined;
  const n = Number(m[1]);
  const unit = m[2] ?? 'y';
  if (unit.startsWith('w')) return n / 52;
  if (unit.startsWith('m')) return n / 12;
  return n;
}

function isGrandparent(p: GuardPerson): boolean {
  const a = ageInYears(p.age);
  if (a !== undefined && a >= GRANDPARENT_MIN_AGE) return true;
  return typeof p.relationship === 'string' && GRANDPARENT_WORD.test(p.relationship);
}

function isAdult(p: GuardPerson): boolean {
  const a = ageInYears(p.age);
  if (a !== undefined && a >= 18) return true;
  if (isGrandparent(p)) return true;
  return typeof p.relationship === 'string' && ADULT_RELATIONSHIP.test(p.relationship);
}

export function castFamilyKinds(cast: GuardPerson[]): Set<FamilyKind> {
  const kinds = new Set<FamilyKind>();
  let children = 0;
  for (const p of cast) {
    const gp = isGrandparent(p);
    const adult = isAdult(p);
    if (gp) kinds.add('grandparent');
    if (adult) kinds.add('grownup');
    if (adult && !gp) {
      kinds.add('parent');
      const rel = typeof p.relationship === 'string' ? p.relationship : '';
      const mum = MOTHER_REL.test(rel);
      const dad = FATHER_REL.test(rel);
      if (mum) kinds.add('mother');
      if (dad) kinds.add('father');
      if (!mum && !dad && !AUNT_UNCLE_REL.test(rel)) {
        // No label, "parent", or a label we do not read: a generic parent who can play mum or dad
        kinds.add('mother');
        kinds.add('father');
      }
    }
    if (!adult) children += 1;
  }
  if (children >= 2) kinds.add('sibling');
  return kinds;
}

/** Words after (or before) a parent word that make it an animal or a personification, never a relative. */
const ANIMAL_PARENT =
  '(?:wolf|wolves|bear|bears|duck|ducks|hen|hens|goose|geese|cat|cats|dog|dogs|bird|birds|tiger|tigers|lion|lions|elephant|elephants|whale|whales|owl|owls|nature|earth)';

const LEXICON: Record<FamilyKind, string> = {
  grandparent: GRANDPARENT_WORD.source + '(?!\\s+clocks?\\b)',
  // Animal parents ("Mother Wolf", "Mama Bear", "Mother Nature") are not relatives; the reversed form ("the wolf
  // mother") is skipped in findHits (PRECEDED_BY): no regex lookbehind, this module runs in browsers without it
  parent: `\\b(?:parent|aunt|auntie|aunty|uncle)s?\\b(?!\\s+${ANIMAL_PARENT}\\b)`,
  mother: `\\b${MOTHER_WORDS}\\b(?!\\s+${ANIMAL_PARENT}\\b|-of-pearl\\b)`,
  father: `\\b${FATHER_WORDS}\\b(?!\\s+${ANIMAL_PARENT}\\b)`,
  grownup: '\\b(?:grown-?ups?|adults?)\\b',
  sibling:
    '\\b(?:siblings?|brothers?|sisters?|cousins?|another child|the other child|both children|the two children)\\b(?!-in-arms\\b|\\s+grimm\\b)',
};
/** Per kind: a hit right after one of these words is skipped (a post-match check instead of a lookbehind). */
const PRECEDED_BY: Partial<Record<FamilyKind, RegExp>> = {
  parent: new RegExp(`\\b${ANIMAL_PARENT}\\s+$`, 'i'),
  mother: new RegExp(`\\b${ANIMAL_PARENT}\\s+$`, 'i'),
  father: new RegExp(`\\b${ANIMAL_PARENT}\\s+$`, 'i'),
};
/**
 * Fixed phrases that start with a relative word but name no relative, tested on the text from the hit onwards (a
 * post-match check, never a lookbehind). Includes the holidays "Mother's Day" / "Father's Day" ("Mother's Day card").
 */
const NON_RELATIVE_IDIOM =
  /^(?:father\s+(?:christmas|time|frost)|mother\s+(?:hubbard|superior|ships?|goose|nature|earth)|papa\s+smurf|brothers?\s+bears?|(?:mother|father)(?:['’]s|s['’]?)?\s+day)\b/i;
const COLLECTIVE ='\\b(?:the whole family|the family|everyone|everybody|every pair of eyes)\\b';
const OFF_FRAME = [
  '\\bedge of the (?:frame|picture|page)\\b',
  '\\boff-(?:page|frame|picture)\\b',
  '\\b(?:hands?|arms?|sleeves?)\\s+only\\b',
  '\\bonly\\s+(?:a\\s+|the\\s+|two\\s+|\\w+\\s+)?(?:hands?|arms?|sleeves?)\\b',
  '\\bcardigan (?:arm|sleeve)s?\\b',
  '\\bfaceless\\b',
  '\\bsilhouette of\\b',
].join('|');

/**
 * A generic "the baby" / "the little one" (opt-in: absentFamilyMentions({ genericBaby: true })): a baby nobody in the
 * photo plays. Animal / object babies ("the baby triceratops", "the baby photo") are skipped by the words that follow.
 */
const GENERIC_BABY =
  '\\bthe (?:baby|little one|tiny one|toddler)\\b(?!\\s+in the (?:photo|picture|photograph)\\b|\\s+and the (?:big|older|bigger|other) one\\b|[\\s-]+(?:triceratops|dino\\w*|dragons?|birds?|bears?|cubs?|foxes|fox|rabbits?|bunny|bunnies|whales?|dolphins?|turtles?|elephants?|tigers?|lions?|ducks?|ducklings?|chicks?|kittens?|puppy|puppies|monkeys?|giraffes?|seals?|otters?|penguins?|owls?|jaguars?|deer|fawn|lambs?|calf|animals?|creatures?|photos?|pictures?|shoes?|socks?|teeth|tooth|blankets?|bottles?|clothes|names?)\\b)';
/** A cast member who can be "the baby": under 2, or labelled baby / newborn / infant / toddler. */
function castHasBaby(cast: GuardPerson[]): boolean {
  return cast.some((p) => {
    const a = ageInYears(p.age);
    if (a !== undefined && a < 2) return true;
    return typeof p.relationship === 'string' && /(?:baby|newborn|infant|toddler)/i.test(p.relationship);
  });
}

const POSSESSIVE_BEFORE = /([A-Za-z][A-Za-z'’-]*)['’]s\s+$/;

type Hit = { index: number; phrase: string };

function findHits(text: string, pattern: string, cast: GuardPerson[], exemptPossessive: boolean, precededBy?: RegExp): Hit[] {
  const names = new Set(cast.map((p) => p.name.trim().toLowerCase()));
  const nameWords = new Set<string>();
  for (const n of names) {
    nameWords.add(n);
    for (const t of n.split(/\s+/)) if (t) nameWords.add(t);
  }
  const hits: Hit[] = [];
  const re = new RegExp(pattern, 'gi');
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m[0].length === 0) {
      re.lastIndex++;
      continue;
    }
    if (precededBy && precededBy.test(text.slice(0, m.index))) continue;
    if (NON_RELATIVE_IDIOM.test(text.slice(m.index))) continue;
    if (exemptPossessive) {
      const pm = POSSESSIVE_BEFORE.exec(text.slice(0, m.index));
      if (pm && !names.has(pm[1].toLowerCase())) continue;
    }
    const phrase = m[0].toLowerCase();
    if (nameWords.has(phrase) || nameWords.has(phrase.replace(/s$/, ''))) continue;
    hits.push({ index: m.index, phrase });
  }
  return hits;
}

export function absentFamilyMentions(
  text: string,
  opts: {
    cast: GuardPerson[];
    allowCollective?: boolean;
    genericBaby?: boolean;
    allowNewborn?: boolean;
    /** Story narration: only invented relatives (no grown-up/adult words, off-frame phrases or collectives). */
    narration?: boolean;
    /** The story's own non-photo relatives/characters (Bonk's mother), whole words, lower-case: never an invention. */
    storyRelatives?: string[];
  },
): string[] {
  const { cast } = opts;
  const present = castFamilyKinds(cast);
  const storyOwn = new Set((opts.storyRelatives || []).map((x) => x.toLowerCase()));
  const hits: Hit[] = [];
  for (const kind of Object.keys(LEXICON) as FamilyKind[]) {
    if (present.has(kind) || (opts.narration && kind === 'grownup')) continue;
    hits.push(...findHits(text, LEXICON[kind], cast, true, PRECEDED_BY[kind]).filter((h) => !storyOwn.has(h.phrase)));
  }
  if (opts.genericBaby && !opts.allowNewborn && !castHasBaby(cast)) hits.push(...findHits(text, GENERIC_BABY, cast, false));
  if (cast.length === 1 && !opts.allowCollective && !opts.narration) hits.push(...findHits(text, COLLECTIVE, cast, false));
  if (!opts.narration) hits.push(...findHits(text, OFF_FRAME, cast, false));
  hits.sort((a, b) => a.index - b.index);
  return [...new Set(hits.map((h) => h.phrase))];
}

const TITLE_ABBREV = /(?:^|[^A-Za-z])(?:mr|mrs|ms|dr|st)\.$/i;

/** Split after . ! ? plus whitespace, except after titles (Mr. Mrs. Ms. Dr. St.). */
function splitSentences(text: string): string[] {
  const out: string[] = [];
  let start = 0;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if ((c === '.' || c === '!' || c === '?') && /\s/.test(text[i + 1] ?? '')) {
      if (c === '.' && TITLE_ABBREV.test(text.slice(start, i + 1))) continue;
      out.push(text.slice(start, i + 1));
      while (/\s/.test(text[i + 1] ?? '')) i++;
      start = i + 1;
    }
  }
  if (start < text.length) out.push(text.slice(start));
  return out;
}

export function scrubAbsentFamily(text: string, opts: Parameters<typeof absentFamilyMentions>[1]): string {
  return splitSentences(text)
    .filter((s) => absentFamilyMentions(s, opts).length === 0)
    .join(' ')
    .trim();
}

export function familyLine(castNames: string[]): string {
  const names =
    castNames.length <= 1
      ? castNames.join('')
      : `${castNames.slice(0, -1).join(', ')} and ${castNames[castNames.length - 1]}`;
  return `The family in this book is exactly: ${names}. Nobody else in the picture is family.`;
}

const OFF_FRAME_WHOLE = new RegExp(`^(?:${OFF_FRAME})$`, 'i');

/** True for a phrase (as absentFamilyMentions returns it) that the off-frame list matches ("silhouette of", "hands only"). */
export function isOffFramePhrase(phrase: string): boolean {
  return OFF_FRAME_WHOLE.test(phrase.trim());
}

/**
 * The note printed after the parent's story idea when it names a relative missing from the photo ('' otherwise).
 * Off-frame words in the idea ("a silhouette of a pirate") never trigger it.
 */
export function ideaFamilyNote(storyIdea: string | undefined, cast: GuardPerson[]): string {
  if (!storyIdea) return '';
  // Crowds ("everyone in the village") are never relatives; for a one-person cast only "the (whole) family" counts
  const words = absentFamilyMentions(storyIdea, { cast, allowCollective: true }).filter((x) => !isOffFramePhrase(x));
  if (cast.length === 1) {
    const fam = /\bthe (?:whole )?family\b/i.exec(storyIdea);
    if (fam) words.push(fam[0].toLowerCase());
  }
  if (words.length === 0) return '';
  const plural = words.length > 1 || /s$|children$/.test(words[0]);
  return `FAMILY: ${familyLine(cast.map((p) => p.name))} The idea mentions ${words.join(', ')}, ${plural ? 'who are' : 'who is'} not in the photo: they may be talked about, phoned or remembered, but they never appear, never speak in a balloon and are never registered.`;
}

/** "A", "A and B", "A, B and C" (the familyLine join). */
export function joinNames(names: string[]): string {
  return names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

// ---------- Typed-relative notice (Task C3) ----------

/** The parent's answer to the notice: tell the story without showing the named relatives. */
export type TypedRelativesChoice = { words: string[]; choice: 'continue' };

type RelativeGroup = { re: string; rel: RegExp; kind?: FamilyKind };

/** The specific-relative lexicon (never collectives, grown-up/adult, off-frame words or "baby"). */
const RELATIVE_GROUPS: RelativeGroup[] = [
  { re: GRANDPARENT_WORD.source + '(?!\\s+clocks?\\b)', rel: GRANDPARENT_WORD, kind: 'grandparent' },
  {
    re: `\\b(?:mum|mom|mummy|mommy|mother|mama|amma)s?\\b(?!\\s+${ANIMAL_PARENT}\\b|-of-pearl\\b)`,
    rel: /\b(?:mum|mom|mummy|mommy|mother|mama|amma)s?\b/i,
    kind: 'parent',
  },
  {
    re: `\\b(?:dad|daddy|father|papa|appa)s?\\b(?!\\s+${ANIMAL_PARENT}\\b)`,
    rel: /\b(?:dad|daddy|father|papa|appa)s?\b/i,
    kind: 'parent',
  },
  { re: '\\b(?:aunt|auntie|aunty)s?\\b', rel: /\b(?:aunt|auntie|aunty)s?\b/i },
  { re: '\\buncles?\\b', rel: /\buncles?\b/i },
  { re: '\\bbrothers?\\b(?!-in-arms\\b|\\s+grimm\\b)', rel: /\bbrothers?\b/i, kind: 'sibling' },
  { re: '\\bsisters?\\b(?!-in-arms\\b)', rel: /\bsisters?\b/i, kind: 'sibling' },
  { re: '\\bcousins?\\b', rel: /\bcousins?\b/i },
];
/** A relationship that names the kind ("parent", "sibling") without naming the group. */
const KIND_RELATIONSHIP: Partial<Record<FamilyKind, RegExp>> = {
  parent: /\bparents?\b/i,
  sibling: /\bsiblings?\b|\btwins?\b/i,
};
const ANIMAL_BEFORE = new RegExp(`\\b${ANIMAL_PARENT}\\s+$`, 'i');
/**
 * Capitalised words after a relative word that are never the relative's name: clause words and common verbs
 * ("Grandma Says hi", "Uncle Visits the zoo", "Mum I").
 */
const NOT_A_NAME = new RegExp(
  '^(?:' +
    [
      'i|a|an|the|and|but|or|so|then|when|while|who|whose|with|at|in|on|to|from|for|of|by|as|if|too|also|again|now|here|there',
      'is|was|are|were|be|has|had|have|does|did|do|will|would|can|could|may|might|must|shall|should',
      'says|said|tells|told|asks|asked|gives|gave|goes|went|comes|came|takes|took|makes|made|helps|helped|visits|visited',
      'loves|loved|likes|liked|sees|saw|finds|found|brings|brought|bakes|baked|cooks|cooked|sings|sang|reads|read',
      'plays|played|shows|showed|waves|waved|hugs|hugged|lives|lived|calls|called|wants|wanted|knows|knew|gets|got',
      'laughs|laughed|smiles|smiled|walks|walked|runs|ran|sits|sat|stays|stayed|teaches|taught|keeps|kept|lets|let',
      'always|never|often|still|just|really|hi|hello|yes|no|oh|wow',
    ].join('|') +
    ')$',
  'i',
);

const titleCase = (w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();

type RelativeHit = { group: RelativeGroup; word: string; name?: string; index: number };

/** Every lexicon hit in idea order (possessive of a non-cast name and animal parents skipped). */
function relativeHits(text: string, castNames: Set<string>): RelativeHit[] {
  const nameWords = new Set<string>();
  for (const n of castNames) {
    nameWords.add(n);
    for (const t of n.split(/\s+/)) if (t) nameWords.add(t);
  }
  const hits: RelativeHit[] = [];
  for (const group of RELATIVE_GROUPS) {
    const re = new RegExp(group.re, 'gi');
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) {
      if (m[0].length === 0) {
        re.lastIndex++;
        continue;
      }
      const before = text.slice(0, m.index);
      if (group.kind === 'parent' && ANIMAL_BEFORE.test(before)) continue;
      if (NON_RELATIVE_IDIOM.test(text.slice(m.index))) continue;
      const pm = POSSESSIVE_BEFORE.exec(before);
      if (pm && !castNames.has(pm[1].toLowerCase())) continue;
      const word = m[0].toLowerCase();
      // Someone in the photo is called "Nana": that is a name, not a relative
      if (nameWords.has(word)) continue;
      const after = /^ +([A-Z][A-Za-z’'-]*)/.exec(text.slice(m.index + m[0].length));
      const name = after ? after[1].replace(/['’]s$/, '') : undefined;
      hits.push({ group, word, index: m.index, ...(name && !NOT_A_NAME.test(name) ? { name } : {}) });
    }
  }
  return hits.sort((a, b) => a.index - b.index);
}

/**
 * The relatives the parent's idea names who are not in the photo, as display words in idea order ("Uncle Ravi",
 * "Grandmother"), deduped case-insensitively. Words also found in `opts.baseline` (our template or suggestion text)
 * are ignored: only what the parent added counts.
 */
export function typedRelativesNotInPhoto(idea: string, cast: GuardPerson[], opts: { baseline?: string } = {}): string[] {
  if (!idea || !idea.trim()) return [];
  const castNames = new Set(cast.map((p) => p.name.trim().toLowerCase()).filter(Boolean));
  const baselineWords = new Set(opts.baseline ? relativeHits(opts.baseline, castNames).map((h) => h.word) : []);
  const anyGroup = (rel: string) => RELATIVE_GROUPS.some((g) => g.rel.test(rel)) || Object.values(KIND_RELATIONSHIP).some((r) => r!.test(rel));
  const rels = cast.map((p) => (typeof p.relationship === 'string' ? p.relationship : '')).filter(Boolean);
  // Kinds filled by people with no family label (a 40-year-old with no relationship is a parent)
  const unlabelledKinds = castFamilyKinds(cast.filter((p) => !(typeof p.relationship === 'string' && anyGroup(p.relationship))));
  const kindLabelled = (kind: FamilyKind) =>
    rels.some((r) => RELATIVE_GROUPS.some((g) => g.kind === kind && g.rel.test(r)) || !!KIND_RELATIONSHIP[kind]?.test(r));

  const out: string[] = [];
  const seen = new Set<string>();
  for (const h of relativeHits(idea, castNames)) {
    if (baselineWords.has(h.word)) continue;
    if (h.name && castNames.has(h.name.toLowerCase())) continue;
    if (rels.some((r) => h.group.rel.test(r))) continue;
    const kind = h.group.kind;
    if (kind && !kindLabelled(kind) && unlabelledKinds.has(kind)) continue;
    const display = h.name ? `${titleCase(h.word)} ${h.name}` : titleCase(h.word);
    const key = display.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(display);
  }
  return out;
}

/** The notice copy: "<A> isn't in your photo. …" / "<A and B> aren't in your photo. …". */
export function typedRelativeNotice(words: string[]): string {
  const plural = words.length > 1 || (words.length === 1 && /s$/i.test(words[0].split(' ')[0]));
  return `${joinNames(words)} ${plural ? "aren't" : "isn't"} in your photo. Use a photo with them, or we'll tell the story without showing them.`;
}

/** Case-insensitive set equality. */
export function sameTypedRelatives(a: string[], b: string[]): boolean {
  const sa = new Set(a.map((w) => w.toLowerCase()));
  const sb = new Set(b.map((w) => w.toLowerCase()));
  return sa.size === sb.size && [...sa].every((w) => sb.has(w));
}

/** True when there is nothing to ask, or the stored choice was made for exactly these words. */
export function typedRelativesCovered(words: string[], choice: TypedRelativesChoice | null | undefined): boolean {
  if (words.length === 0) return true;
  return !!choice && choice.choice === 'continue' && Array.isArray(choice.words) && sameTypedRelatives(choice.words, words);
}

/** The create flows' people (drafts or payload characters) as guard people: a main character's relationship is dropped, as in the payload. */
export function guardCast(
  people: Array<{ name: string; age?: number | string | null; relationship?: string | null; isMainCharacter?: boolean }>,
): GuardPerson[] {
  return people.map((p) => ({
    name: (p.name || '').trim(),
    age: p.age,
    relationship: p.isMainCharacter ? undefined : (p.relationship || '').trim() || undefined,
    isMainCharacter: !!p.isMainCharacter,
  }));
}
